/**
 * Service Tag Lookup Modal Component
 * ===================================
 * Modal for looking up device info by Dell/HP/Lenovo service tag using AI.
 * Shows preview of extracted data before applying to form.
 */

import { useState, useEffect, useCallback } from 'react'
import { X, Loader2, Check, AlertCircle, ExternalLink, Tag } from 'lucide-react'
import { serviceTagApi } from '@/services/api'
import type { ServiceTagLookupResponse } from '@/services/api'

interface ServiceTagLookupModalProps {
  serviceTag: string
  manufacturer?: string
  onApply: (data: {
    name?: string
    description?: string
    manufacturer?: string
  }) => void
  onClose: () => void
}

export default function ServiceTagLookupModal({ serviceTag, manufacturer, onApply, onClose }: ServiceTagLookupModalProps) {
  const [result, setResult] = useState<ServiceTagLookupResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Track which fields user wants to apply
  const [applyFields, setApplyFields] = useState({
    name: true,
    description: true,
    manufacturer: true,
  })

  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // Perform service tag lookup when modal opens
  useEffect(() => {
    const lookupServiceTag = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await serviceTagApi.lookup(serviceTag, manufacturer)
        setResult(response)
        if (response.error) {
          setError(response.error)
        } else if (!response.model) {
          setError('Could not find device information for this service tag.')
        }
      } catch (err) {
        console.error('Service tag lookup failed:', err)
        setError('Failed to look up service tag. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    lookupServiceTag()
  }, [serviceTag, manufacturer])

  // Handle apply
  const handleApply = useCallback(() => {
    if (!result?.model) return

    const data: Parameters<typeof onApply>[0] = {}

    // Build the name from manufacturer + model
    if (applyFields.name && result.model) {
      const name = result.manufacturer
        ? `${result.manufacturer} ${result.model}`
        : result.model
      data.name = name
    }

    // Build description from specs and warranty info
    if (applyFields.description) {
      const descParts: string[] = []
      if (result.specs) {
        descParts.push(result.specs)
      }
      if (result.ship_date) {
        descParts.push(`Ship Date: ${result.ship_date}`)
      }
      if (result.warranty_end) {
        descParts.push(`Warranty End: ${result.warranty_end}`)
      }
      if (result.warranty_status) {
        descParts.push(`Warranty Status: ${result.warranty_status}`)
      }
      if (descParts.length > 0) {
        data.description = descParts.join('\n')
      }
    }

    if (applyFields.manufacturer && result.manufacturer) {
      data.manufacturer = result.manufacturer
    }

    onApply(data)
    onClose()
  }, [result, applyFields, onApply, onClose])

  // Confidence badge color
  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    }
  }

  // Format warranty status with icon
  const getWarrantyStatusDisplay = (status: string | null) => {
    if (!status) return null
    const isActive = status.toLowerCase().includes('active') || status.toLowerCase().includes('in warranty')
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        isActive
          ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
          : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
      }`}>
        {isActive ? <Check size={12} /> : <X size={12} />}
        {status}
      </span>
    )
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/50 rounded-lg">
              <Tag size={20} className="text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Service Tag Lookup
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Tag: <span className="font-mono">{serviceTag}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Close (Esc)"
          >
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 size={40} className="animate-spin text-primary-500 mb-4" />
              <p className="text-gray-600 dark:text-gray-300 font-medium">Looking up device info...</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Searching Dell/HP/Lenovo support sites with AI
              </p>
            </div>
          ) : error && !result?.model ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle size={40} className="text-red-500 mb-4" />
              <p className="text-red-600 dark:text-red-400 font-medium">{error}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Try checking the service tag or enter details manually
              </p>
            </div>
          ) : result?.model ? (
            <div className="space-y-6">
              {/* Confidence indicator, data source, and manufacturer */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${getConfidenceColor(result.confidence)}`}>
                  {result.confidence.toUpperCase()} CONFIDENCE
                </span>
                {/* Data source badge */}
                {result.source === 'dell_api' ? (
                  <span className="text-xs font-medium px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300">
                    Dell Official API
                  </span>
                ) : (
                  <span className="text-xs font-medium px-2 py-1 rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300">
                    AI Lookup
                  </span>
                )}
                {result.manufacturer && (
                  <span className="text-xs font-medium px-2 py-1 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                    {result.manufacturer}
                  </span>
                )}
                {result.source_url && (
                  <a
                    href={result.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                  >
                    View source <ExternalLink size={12} />
                  </a>
                )}
              </div>

              {/* Extracted fields */}
              <div className="space-y-4">
                {/* Model Name */}
                <div className="p-4 border dark:border-gray-700 rounded-lg">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Device Model
                      </label>
                      <p className="mt-1 text-gray-900 dark:text-white font-medium text-lg">
                        {result.manufacturer && `${result.manufacturer} `}{result.model}
                      </p>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={applyFields.name}
                        onChange={(e) => setApplyFields(prev => ({ ...prev, name: e.target.checked }))}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-600 dark:text-gray-400">Apply as Name</span>
                    </label>
                  </div>
                </div>

                {/* Manufacturer */}
                {result.manufacturer && (
                  <div className="p-4 border dark:border-gray-700 rounded-lg">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Manufacturer
                        </label>
                        <p className="mt-1 text-gray-900 dark:text-white font-medium">
                          {result.manufacturer}
                        </p>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={applyFields.manufacturer}
                          onChange={(e) => setApplyFields(prev => ({ ...prev, manufacturer: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Apply</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Specs/Description */}
                {(result.specs || result.description || result.ship_date || result.warranty_end) && (
                  <div className="p-4 border dark:border-gray-700 rounded-lg">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Device Details
                        </label>
                        <div className="mt-2 space-y-2">
                          {result.specs && (
                            <p className="text-gray-700 dark:text-gray-300 text-sm">
                              {result.specs}
                            </p>
                          )}
                          {result.description && !result.specs && (
                            <p className="text-gray-700 dark:text-gray-300 text-sm">
                              {result.description}
                            </p>
                          )}
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            {result.ship_date && (
                              <div className="text-sm">
                                <span className="text-gray-500 dark:text-gray-400">Ship Date:</span>{' '}
                                <span className="text-gray-700 dark:text-gray-300">{result.ship_date}</span>
                              </div>
                            )}
                            {result.warranty_end && (
                              <div className="text-sm">
                                <span className="text-gray-500 dark:text-gray-400">Warranty End:</span>{' '}
                                <span className="text-gray-700 dark:text-gray-300">{result.warranty_end}</span>
                              </div>
                            )}
                          </div>
                          {result.warranty_status && (
                            <div className="mt-2">
                              {getWarrantyStatusDisplay(result.warranty_status)}
                            </div>
                          )}
                        </div>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={applyFields.description}
                          onChange={(e) => setApplyFields(prev => ({ ...prev, description: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Apply as Description</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {result?.model && (
              <>
                {[
                  applyFields.name && result.model && 'name',
                  applyFields.description && (result.specs || result.description || result.ship_date || result.warranty_end) && 'description',
                  applyFields.manufacturer && result.manufacturer && 'manufacturer',
                ].filter(Boolean).length} fields selected
              </>
            )}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={!result?.model}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                result?.model
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }`}
            >
              <Check size={16} />
              Apply to Form
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
